<template>
  <div class="citToolbar">
    <span
      v-for="tool in config"
      :key="tool.name">
      <Tool
        :tool="tool"
        @setValue="setToolValue"
        @setOption="setToolOption"
        @setActive="setToolActive" />
      <span v-if="toolbarGroup.includes(tool.name)" class="groupBar"></span>
    </span>
  </div>
</template>

<script>
import regExp from '@/common/regExp'
import Tool from '@/components/CITEditor/Toolbar/Index.vue'

const toolbarInfo = [
  {
    name: 'Bold',
    tag: ['strong', 'b'],
    active: false
  },
  {
    name: 'Italic',
    tag: ['i'],
    active: false
  },
  {
    name: 'Underline',
    tag: ['u'],
    active: false
  },
  {
    name: 'Strikethrough',
    tag: ['strike', 's'],
    active: false
  },
  {
    name: 'FontSize',
    tag: ['span'],
    value: 'default',
    option: false,
    active: false
  },
  {
    name: 'FontFamily',
    tag: ['span'],
    value: '',
    option: false,
    active: false
  },
  {
    name: 'TextAlign',
    value: 'left',
    option: false,
    active: false
  },
  {
    name: 'LineHeight',
    value: 'default',
    option: false,
    active: false
  },
  {
    name: 'Color',
    tag: ['span'],
    value: 'rgba(62,62,62,1)',
    option: false,
    active: false
  },
  {
    name: 'BackgroundColor',
    tag: ['span'],
    value: 'rgba(255,255,255,1)',
    option: false,
    active: false
  },
  {
    name: 'Link',
    tag: ['a'],
    value: '',
    option: false,
    active: false
  },
  {
    name: 'UnorderedList',
    tag: ['ul'],
    active: false
  },
  {
    name: 'OrderedList',
    tag: ['ol'],
    active: false
  },
  {
    name: 'TextIndent',
    tag: ['blockquote'],
    active: false
  },
  {
    name: 'TextOutdent',
    active: false
  },
  {
    name: 'Notranslate',
    tag: ['sub'],
    active: false
  },
  {
    name: 'DataVariable',
    tag: ['span'],
    value: '',
    option: false
  }
]
const toolbarDefault = [
  'Bold', 'Italic', 'Underline', 'Strikethrough',
  'FontSize', 'FontFamily',
  'TextAlign', 'LineHeight',
  'Color', 'BackgroundColor',
  'Link',
  'UnorderedList', 'OrderedList', 'TextIndent', 'TextOutdent',
  'Notranslate', 'DataVariable'
]

export default {
  name: 'CITEditorToolbar',
  props: [
    'id',
    'toolbar',
    'toolbarOption',
    'toolbarState'
  ],
  components: {
    Tool
  },
  watch: {
    toolbarState (state) {
      if (!state) {
        this.setOptionState()
      }
    }
  },
  mounted () {
    const toolbar = this.toolbarOption ? this.toolbarOption : toolbarDefault
    const defaultInfo = this._.cloneDeep(toolbarInfo)
    this.config = defaultInfo.filter(x => toolbar.includes(x.name))
    // this.config = toolbarInfo.filter(x => toolbar.includes(x.name))
    this.changeSelection()
  },
  data () {
    return {
      config: [],
      toolbarGroup: [
        'Strikethrough',
        'FontFamily',
        'LineHeight',
        'BackgroundColor',
        'TextOutdent'
      ]
    }
  },
  methods: {
    changeSelection () {
      document.onselectionchange = () => {
        const selection = window.getSelection()
        if (selection.baseNode !== null) {
          const parent = selection.baseNode.parentElement
          if (parent.className === 'citContents' || parent.closest('.citContents') !== null) {
            this.checkTags()
          }
        }
      }
    },
    // set option state:: all close
    setOptionState () {
      this.config.forEach(function (x) {
        if (x.option) {
          x.option = false
        }
      })
    },
    checkTags () {
      const selection = window.getSelection()
      if (!selection.isCollapsed) {
        this.hasTag()
      } else {
        this.setOptionState()
        this.isTag()
      }
    },
    /**
     * selection isCollapsed
     * (selection의 anchor와 focus가 동일할 때)
     * 노드이름을 받아 툴바에서 탐색
     * active 처리
     * return {
     *   태그명 : false
     * }
    */
    isTag () {
      const selection = window.getSelection()
      const tools = this.config
      let tags = []
      let values = []
      function findTag (node) {
        const nodeName = node.nodeName.toLowerCase()
        if (node.className !== 'citContents') {
          tags.push(nodeName)
          findTag(node.parentNode)
        }
      }
      function findValue (node) {
        if (node.className !== 'citContents' && (node.style.length > 0 || node.dataset)) {
          const length = node.style.length
          const dataVariable = node.dataset && node.dataset.variable
          for (let i = 0; i < length; i++) {
            const key = regExp.camelize(node.style[i])
            const value = key === 'fontSize' ? Number(node.style[key].replace('px', '')) : node.style[key]
            if (key && value) {
              values.push({ name: key, value: value })
            }
          }
          if (dataVariable) {
            values.push({ name: 'dataVariable', value: dataVariable })
          }
          findValue(node.parentNode)
        }
      }
      // 셀렉션에 포함되는 모든 노드이름(=태그명)
      findTag(selection.focusNode.parentNode)
      // 셀렉션에 포함되는 모든 노드 스타일
      findValue(selection.focusNode.parentNode)
      this.config = tools.map((tool) => {
        const setValue = values.find(v => v.name.toLowerCase() === tool.name.toLowerCase())
        return {
          ...tool,
          ...tool.tag && {
            active: tool.tag.find(t => tags.includes(t))
          },
          ...tool.value && {
            value: setValue ? setValue.value : toolbarInfo.find(i => i.name === tool.name).value
          },
          ...tool.name === 'TextAlign' && {
            value: setValue ? setValue.value : 'left'
          },
          ...tool.name === 'DataVariable' && {
            value: setValue ? setValue.value : ''
          }
        }
      })
    },
    /**
     * selection !isCollapsed
     * (selection의의 anchor와 focus가 다르고 범위가 있을 때)
     * 각 툴바에서 사용하는 태그가 범위 내 컨텐츠에 포함되어 있는지 탐색 (arr)
     * active 처리
     * return {
     *    [...태그명] : false
     * }
    */
    hasTag () {
      const selection = window.getSelection()
      const range = selection.getRangeAt(0)
      const clone = range.cloneContents()
      const tools = this.config
      let values = []
      // clone Fragment에서 각 툴바의 태그에 속하는 엘리먼트들만 리턴
      const tags = tools.filter(function (x) {
        if (x.tag) {
          const findTag = x.tag.filter(function (t) {
            const el = clone.querySelectorAll(`${t}`)
            return el.length > 0
          })
          return findTag.length > 0
        }
      })
      if (tags && tags.length > 0) {
        const names = tags.map(x => x.name)
        // 툴바의 태그에 속하는 엘리먼트 array와 매칭하여 active 처리
        tools.forEach(function (tool) {
          tool.active = names.includes(tool.name)
        })
      }
      function findValue (node) {
        if (node.className !== 'citContents' && ((node.style && node.style.length > 0) || node.dataset)) {
          const length = node.style.length
          const dataVariable = node.dataset && node.dataset.variable
          for (let i = 0; i < length; i++) {
            const key = regExp.camelize(node.style[i])
            const value = key === 'fontSize' ? Number(node.style[key].replace('px', '')) : node.style[key]
            if (key && value) {
              values.push({ name: key, value: value })
            }
          }
          if (dataVariable) {
            values.push({ name: 'dataVariable', value: dataVariable })
          }
          findValue(node.parentNode)
        }
      }
      findValue(selection.focusNode.parentNode)
      this.config = tools.map((tool) => {
        const setValue = values.find(v => v.name.toLowerCase() === tool.name.toLowerCase())
        return {
          ...tool,
          ...tool.tag && {
            active: tool.tag.find(t => tags.includes(t))
          },
          ...tool.value && {
            value: setValue ? setValue.value : toolbarInfo.find(i => i.name === tool.name).value
          },
          ...tool.name === 'TextAlign' && {
            value: setValue ? setValue.value : 'left'
          },
          ...tool.name === 'DataVariable' && {
            value: setValue ? setValue.value : ''
          }
        }
      })
      return tags.length > 0 ? tags.map(x => x.name) : false
    },
    setToolValue (data) {
      const tool = this.config.find(x => x.name === data.name)
      if (tool) tool.value = data.value
    },
    setToolOption (data) {
      const tool = this.config.find(x => x.name === data.name)
      this.config.forEach(x => {
        x.option = false
      })
      if (tool) tool.option = data.state
    },
    setToolActive (data) {
      const tool = this.config.find(x => x.name === data.name)
      if (tool) tool.active = data.active
    }
  }
}
</script>

<style lang="scss">
.citToolbar {
  display: flex;
  flex-wrap: wrap;
  position: fixed;
  top: 50%;
  left: 50%;
  margin-top: -0.3em;
  white-space: nowrap;
  border: 1px solid $color-lgrey2;
  background-color: $color-white;
  box-shadow: 0 0 5px rgba(0,0,0,0.15);
  transform: translate(0, -100%);
  z-index: 99;
  .toolOption {
    display: inline-block;
    position: relative;
    vertical-align: middle;
    color: $color-dark;
    z-index: 1;
    & > .toolbarBtn:after {
      content: "";
      display: inline-block;
      width: 12px;
      height: 12px;
      margin-left: 3px;
      vertical-align: middle;
      background: url('~@/assets/img/CITEditor/dropdown_arrow.svg') 50% 50% / contain no-repeat;
      opacity: 0.5;
    }
  }
  .groupBar {
    display: inline-block;
    width: 1px;
    height: 36px;
    vertical-align: middle;
    background-color: $color-lgrey2;
  }
  .toolbarBtn {
    margin: 3px;
    padding: 5px;
    font-size: 0;
    transition: all 0.3s;
    &.active {
      background-color: rgba($color-lgrey2, 0.5);
    }
    img {
      width: 20px;
      opacity: 0.5;
    }
    &.active img,
    &:hover img {
      opacity: 0.8;
    }
  }
  .toolbarColor {
    position: absolute;
    top: 100%;
    left: -10px;
    white-space: normal;
  }
  .optionGroup {
    position: absolute;
    top: 100%;
    left: -10px;
    min-width: 120px;
    max-width: 160px;
    max-height: 200px;
    font-size: 14px;
    overflow: auto;
    background-color: $color-white;
    box-shadow: 0 0 5px rgba(0,0,0,0.2);
    .optionItem {
      display: block;
      width: 100%;
      padding: 10px;
      line-height: 1;
      text-align: left;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      &:hover {
        background-color: rgba($color-lgrey2, 0.2);
      }
      &.active {
        background-color: rgba($color-lgrey, 0.3);
        box-shadow: inset 0 0 5px rgba($color-lgrey, 0.5);
      }
      & + .optionItem {
        border-top: 1px solid rgba($color-lgrey2, 0.5);
      }
    }
  }
}
</style>
